"use client";

import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';

import { useAuth } from './auth-context';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  data: any;
  read: boolean;
  createdAt: string;
}

interface NotificationContextType {
  notifications: Notification[];
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  unreadCount: number;
  isLoading: boolean;
  refreshNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  addNotification: (notification: Notification) => void;
  notificationPermission: NotificationPermission;
  requestNotificationPermission: () => void;
  markAsUnread: (notificationId: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>(typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'default');

  // Helper to recalculate unread count from notifications array
  // Only count notifications that are actually displayed (non-empty title/message)
  const recalcUnreadCount = (notifs: Notification[]) =>
    notifs.filter(n => !n.read && n.title?.trim() && n.message?.trim()).length;

  // Fetch notifications from API
  const refreshNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    try {
      setIsLoading(true);
      
      const response = await fetch('/api/notifications?limit=50', { credentials: 'include' });
      let personalNotifications = [];
      if (response.ok) {
        const data = await response.json();
        personalNotifications = data.notifications;
      } else {
        console.error('NotificationContext: Failed to fetch personal notifications:', response.status);
      }

      // Merge and sort all notifications (admin task notifications are handled
      // separately by AdminNotificationContext and the NotificationDropdown)
      const allNotifications = [...personalNotifications];
      allNotifications.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      setNotifications(allNotifications);
      setUnreadCount(recalcUnreadCount(allNotifications));
      
    } catch (error) {
      console.error('❌ NotificationContext: Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  // Only depend on isAuthenticated — user object reference changes on every render
  // and would cause infinite re-fetches. user is only used for debug logging.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    // Optimistic update — immediately reflect in UI
    setNotifications(prev => {
      const updated = prev.map(n => n.id === notificationId ? { ...n, read: true } : n);
      setUnreadCount(recalcUnreadCount(updated));
      return updated;
    });

    // Message notifications are virtual (msg- prefix) — mark the underlying
    // conversation messages as read so the DB stays in sync and polling doesn't
    // resurrect the notification on the next 30-second refresh cycle.
    if (notificationId.startsWith('msg-')) {
      const notification = notifications.find(n => n.id === notificationId);
      const conversationId = notification?.data?.conversationId;
      if (conversationId) {
        try {
          await fetch(`/api/conversations/${conversationId}/mark-read`, {
            method: 'POST',
            credentials: 'include',
          });
        } catch {
          // Best-effort; the optimistic update still stands
        }
      }
      return;
    }

    try {
      await fetch(`/api/notifications/${notificationId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ read: true }),
      });
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      // Revert on failure
      await refreshNotifications();
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    // Optimistic update — immediately reflect in UI before any network call
    setNotifications(prev => {
      const updated = prev.map(n => ({ ...n, read: true }));
      setUnreadCount(0);
      return updated;
    });

    try {
      // Collect unique conversation IDs from msg-* virtual notifications
      // so we can persist isRead on the underlying Message rows.
      // Without this step the 30-second poll re-injects them as unread.
      const msgNotifications = notifications.filter(n => n.id.startsWith('msg-'));
      const conversationIds = [
        ...new Set(
          msgNotifications
            .map((n: any) => n.data?.conversationId)
            .filter(Boolean)
        ),
      ];

      await Promise.all([
        // Mark every message conversation as read in the DB
        ...conversationIds.map((convId: string) =>
          fetch(`/api/conversations/${convId}/mark-read`, {
            method: 'POST',
            credentials: 'include',
          }).catch(() => {})
        ),
        // Mark all regular DB notifications as read
        fetch('/api/notifications', {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'markAsRead' }),
        }),
      ]);

      // Safe to refresh now — messages are persisted as read so msg-* rows
      // will not reappear on the next polling cycle.
      await refreshNotifications();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      await refreshNotifications();
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId: string) => {
    // Optimistic update — remove immediately
    setNotifications(prev => {
      const updated = prev.filter(n => n.id !== notificationId);
      setUnreadCount(recalcUnreadCount(updated));
      return updated;
    });

    // Message notifications are virtual — nothing to delete in the notification table
    if (notificationId.startsWith('msg-')) return;

    try {
      await fetch(`/api/notifications/${notificationId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Failed to delete notification:', error);
      await refreshNotifications();
    }
  };

  // Add new notification (for real-time updates) with duplicate check
  const addNotification = useCallback((notification: Notification) => {
    setNotifications(prev => {
      if (prev.some(n => n.id === notification.id)) return prev;
      const merged = [notification, ...prev];
      setUnreadCount(recalcUnreadCount(merged));
      return merged;
    });
  // setState functions are stable — no deps needed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to request browser notification permission
  const requestNotificationPermission = () => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then((permission) => {
        setNotificationPermission(permission);
      });
    }
  };

  // Mark notification as unread
  const markAsUnread = async (notificationId: string) => {
    // Optimistic update
    setNotifications(prev => {
      const updated = prev.map(n => n.id === notificationId ? { ...n, read: false } : n);
      setUnreadCount(recalcUnreadCount(updated));
      return updated;
    });

    if (notificationId.startsWith('msg-')) return; // Message notifications don't support unread toggle

    try {
      await fetch(`/api/notifications/${notificationId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ read: false }),
      });
    } catch (error) {
      console.error('Failed to mark notification as unread:', error);
      await refreshNotifications();
    }
  };

  // Update permission state if it changes elsewhere
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  // Keep unreadCount always derived from the notifications list.
  // This is the safety net — individual mutations also call setUnreadCount
  // optimistically, but this effect ensures they can never drift out of sync.
  useEffect(() => {
    setUnreadCount(recalcUnreadCount(notifications));
  }, [notifications]);

  // Initial load when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refreshNotifications();
    } else {
      // Clear notifications when user logs out
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated, refreshNotifications]);

  // Poll for new notifications every 30 seconds during authenticated session
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(refreshNotifications, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, refreshNotifications]);

  // Set up real-time notifications if available
  useEffect(() => {
    if (!isAuthenticated) return;

    // The dev server handles one request at a time. An open SSE stream
    // holds that slot, so avatars, thumbnails, and the bell never finish.
    // The 30-second poll above still loads notifications.
    if (process.env.NODE_ENV === "development") return;

    // Only set up Server-Sent Events if the browser supports it
    if (typeof EventSource !== 'undefined') {
      let eventSource: EventSource | null = null;
      let closed = false;
      
      try {
        eventSource = new EventSource('/api/notifications/stream');
        
        eventSource.onopen = () => {
          if (process.env.NODE_ENV === "development") console.log('NotificationContext: SSE connection established');
        };
        
        eventSource.onmessage = (event) => {
          try {
            const notification = JSON.parse(event.data);
            addNotification(notification);
            
            // Show browser notification if permission granted and app is not focused
            if ('Notification' in window && 
                Notification.permission === 'granted' && 
                document.hidden) {
              new Notification(notification.title, {
                body: notification.message,
                icon: '/icon-192.png',
                tag: notification.id,
              });
            }
          } catch (error) {
            console.error('Failed to parse notification:', error);
          }
        };

        eventSource.onerror = (error) => {
          if (closed) return;
          // EventSource auto-reconnects on network errors; close it ourselves
          // to prevent a runaway reconnect loop (e.g. when the stream returns 401).
          if (eventSource?.readyState !== EventSource.CONNECTING) {
            console.error('Notification stream error:', error);
          }
          // Stop retrying — the 30-second poll provides fallback coverage
          eventSource?.close();
        };

        return () => {
          closed = true;
          eventSource?.close();
        };
      } catch (error) {
        console.error('Failed to set up notification stream:', error);
      }
    }
  }, [isAuthenticated, addNotification]);

  const value: NotificationContextType = {
    notifications,
    setNotifications,
    unreadCount,
    isLoading,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    addNotification,
    notificationPermission,
    requestNotificationPermission,
    markAsUnread,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
} 